import { Injectable, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';

/** `skiss:` namespace prefix for the custom domain icon set. */
export const SKISS_ICON_NAMESPACE = 'skiss';

/**
 * Every SVG in `src/icons/` is inlined at build time and registered under the
 * `skiss` namespace, so `<mat-icon svgIcon="skiss:sailing-yacht">` works.
 *
 * Inlining rather than fetching by URL is deliberate. Registering a URL makes
 * `MatIconRegistry` issue an HTTP request, and this app serves `index.html` for
 * any unmatched path — so a missing icon resolved to a page of HTML and threw
 * "Error retrieving icon … <svg> tag not found" on every render. Globbing the
 * directory means only icons that genuinely exist are ever registered.
 */
const ICON_SOURCES = import.meta.glob('/src/icons/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

@Injectable({ providedIn: 'root' })
export class IconRegistryService {
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);

  /** Icon names found on disk, without the namespace prefix. */
  private readonly available = new Set(
    Object.keys(ICON_SOURCES).map(path => path.split('/').pop()!.replace('.svg', ''))
  );

  /** Registers the namespace. Called once during app bootstrap. */
  public register(): void {
    for (const [path, contents] of Object.entries(ICON_SOURCES)) {
      const name = path.split('/').pop()!.replace('.svg', '');
      this.iconRegistry.addSvgIconLiteralInNamespace(
        SKISS_ICON_NAMESPACE,
        name,
        this.sanitizer.bypassSecurityTrustHtml(contents)
      );
    }
  }

  /**
   * Whether an icon reference can actually be rendered.
   *
   * Callers use this before binding `svgIcon`, because Material throws for an
   * unregistered name. Data may legitimately reference a `skiss:` icon whose
   * artwork has not been exported yet; it simply renders as blank until it is.
   */
  public isAvailable(reference: string | undefined): boolean {
    return !!this.resolve(reference);
  }

  /**
   * Turn whatever the data holds into a name `svgIcon` will accept.
   *
   * `course.icon` stores a **bare** name ("sailing-yacht") — the backoffice
   * picker writes what the artwork is called, not what one client happens to
   * prefix it with, so the same value works in both apps. Older rows may carry
   * the namespaced form, so both are accepted and normalised here rather than
   * at every call site.
   */
  public resolve(reference: string | undefined): string | null {
    if (!reference) {
      return null;
    }
    const bare = reference.startsWith(`${SKISS_ICON_NAMESPACE}:`)
      ? reference.slice(SKISS_ICON_NAMESPACE.length + 1)
      : reference;
    return this.available.has(bare) ? `${SKISS_ICON_NAMESPACE}:${bare}` : null;
  }

  /**
   * The first of several candidate names that has artwork.
   *
   * Icon names are authored in the backoffice, so a group or product can point
   * at something never exported — `skiss:certificate` and `skiss:diving` both
   * do today. Callers pass their preference and a fallback rather than testing
   * each one at the call site.
   */
  public resolveFirst(...references: (string | null | undefined)[]): string | null {
    for (const reference of references) {
      const resolved = this.resolve(reference ?? undefined);
      if (resolved) {
        return resolved;
      }
    }
    return null;
  }

  /** Names currently registered, for the design-system gallery. */
  public registeredNames(): string[] {
    return [...this.available].sort();
  }

  /**
   * True for a Material Symbols ligature.
   *
   * Three cases, and the third is the one that bites: a name that resolves is
   * ours, a namespaced name that does *not* resolve is still ours — artwork we
   * have not exported yet — and only a bare, unresolved name may be handed to
   * Material. Without the middle case, `skiss:diving` fell through and Material
   * rendered the string "skiss:diving" on the page.
   */
  public isLigature(reference: string | undefined): boolean {
    if (!reference || this.resolve(reference)) {
      return false;
    }
    return !reference.startsWith(`${SKISS_ICON_NAMESPACE}:`);
  }
}
